import { get } from 'lodash-es';

import { createModelSegment } from 'client/data/luckdragon/segment';
import { parseContent } from 'client/data/cms/content';

import { random } from 'client/utils/seed-randomizers';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { isUsedState } from 'site-modules/shared/utils/publication-states';
import { HOME_PAGE_CONTENT_PATH } from 'site-modules/shared/constants/home-page';

import { VisitorHistoryModel } from './visitor-history';
import { UserProfileModel } from './profile/profile';
import { CmsModel } from './cms';
import { buildMakeModelDefaultYear, buildMmyPubStatePath, VehicleModel } from './vehicle-v2';

export const SuggestedVehicleModel = createModelSegment('suggestedVehicle', [
  {
    path: 'forcedVehicleParams',
  },
  {
    path: 'vehicleParams["{isAuthenticated}"]',
    async resolve(match, context) {
      const forcedVehicleParams = context.getValue('forcedVehicleParams');
      if (forcedVehicleParams) {
        return Promise.resolve(forcedVehicleParams);
      }

      const { isAuthenticated } = match;
      const [insiderProfile, aggregatedHistory, homeContent] = await Promise.all([
        isAuthenticated === 'true' && context.resolveValue('data.idm', UserProfileModel).catch(() => {}),
        context.resolveValue('aggregatedHistory.size["1"].isVinOnly["false"]', VisitorHistoryModel).catch(() => {}),
        context.resolveValue(`content["${HOME_PAGE_CONTENT_PATH}"]`, CmsModel).catch(() => {}),
      ]);
      const content = parseContent(homeContent).child('home-seo-footer');

      let params;
      // get vehicle from insider profile
      const insiderVehicle = get(insiderProfile, 'mergedAttributes.makeModels[0]');
      if (insiderVehicle) {
        params = {
          make: makeNiceName(insiderVehicle.make),
          model: makeNiceName(insiderVehicle.model),
          year: insiderVehicle.year,
        };
      }

      // get vehicle from udm profile
      const udmVehicles = get(aggregatedHistory, 'recentlyViewedMakeModels');
      if (!params && udmVehicles && udmVehicles.length) {
        const udmVehicle = udmVehicles[0];
        params = {
          make: udmVehicle.makeNiceId,
          model: udmVehicle.modelNiceId,
          year: udmVehicle.year,
        };
      }

      // if the vehicle from the profile is a used year, then try the "defaultYear" for that make/model.
      // if the "defaultYear" is still used, then we should need to fall back to a random vehicle
      // from the "popular new cars" list.
      if (params) {
        const isUsed =
          !params.year || isUsedState(await context.resolveValue(buildMmyPubStatePath(params), VehicleModel));

        if (isUsed) {
          const defaultYear = await context.resolveValue(buildMakeModelDefaultYear(params), VehicleModel);
          const isDefaultYearUsed =
            defaultYear === params.year &&
            (!defaultYear ||
              isUsedState(
                await context.resolveValue(buildMmyPubStatePath({ ...params, year: defaultYear }), VehicleModel)
              ));

          if (isDefaultYearUsed) {
            params = null;
          } else {
            params.year = defaultYear;
          }
        }
      }

      // fallback to a random "most popular new vehicle" from the list we display on the homepage
      if (!params && content && content.hasChild('popular-new-cars-seo-footer')) {
        const popularVehicles = content.child('popular-new-cars-seo-footer').links();
        const randomIndex = Math.floor(random() * (popularVehicles.length - 1));
        const makeModel = popularVehicles[randomIndex].href.split('/');
        params = {
          make: makeModel[1],
          model: makeModel[2],
        };
      }

      if (params && !params.year) {
        params.year = await context.resolveValue(buildMakeModelDefaultYear(params), VehicleModel);
      }
      return params;
    },
  },
]);
